import { useState } from "react";
import { Link } from "react-router";

import { useApiOne } from "../api/hooks";
import { Bars, Money, Page } from "../components/ui";
import type { Dashboard } from "../api/types";

/** `/admin/dashboard` (02 §16): counters, money, and a daily sales series. */
export function DashboardPage() {
  const [days, setDays] = useState(7);
  const { data, loading, error } = useApiOne<Dashboard>("/admin/dashboard", { days });

  return (
    <Page
      title="Dashboard"
      actions={
        <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      }
    >
      {error && <p className="error">{error}</p>}
      <div className="stats">
        <Stat label="Orders today" value={data?.orders_today} to="/orders" />
        <Stat label="Active orders" value={data?.orders_active} to="/orders?type=active" />
        <Stat label="Sales today" value={data && <Money value={data.sales_today} />} />
        <Stat label="Sales this month" value={data && <Money value={data.sales_month} />} />
        <Stat label="Platform balance" value={data && <Money value={data.platform_balance} />} to="/earnings" />
        <Stat label="Pending remittances" value={data && <Money value={data.pending_remittances} />} to="/remittances" />
        <Stat label="Customers" value={data?.customers} to="/users?role=client" />
        <Stat label="Active vendors" value={data?.vendors_active} to="/vendors" />
        <Stat label="Drivers online" value={data?.drivers_online} to="/users?role=driver" />
        <Stat label="Orders total" value={data?.orders_total} />
      </div>
      <div className="grid">
        <section className="card">
          <h2>Daily sales</h2>
          {loading && !data ? <p>Loading…</p> : null}
          {data && data.series.length === 0 && <p className="muted">No orders in this period.</p>}
          {data && data.series.length > 0 && (
            <Bars points={data.series} valueOf={(p) => Number.parseFloat((p as Dashboard["series"][number]).sales)} format={(n) => <Money value={n} />} />
          )}
        </section>
        <section className="card">
          <h2>Daily orders</h2>
          {data && data.series.length > 0 && <Bars points={data.series} valueOf={(p) => (p as Dashboard["series"][number]).orders} />}
        </section>
      </div>
    </Page>
  );
}

function Stat({ label, value, to }: { label: string; value: React.ReactNode; to?: string }) {
  const inner = (
    <>
      <span className="stat-value">{value ?? "…"}</span>
      <span className="stat-label">{label}</span>
    </>
  );
  return to ? (
    <Link to={to} className="stat">
      {inner}
    </Link>
  ) : (
    <div className="stat">{inner}</div>
  );
}
